import type { BrandSpecials } from '@iheartjane/data-access';
import type { CardCarouselProps } from '@iheartjane/menu';
import {
  CardCarousel,
  JaneGoldCard,
  OhioGoldEducation,
  getSpecialSlug,
} from '@iheartjane/menu';
import { Flex, JaneGoldIcon, Typography } from '@iheartjane/reefer';
import type { Nullable } from '@iheartjane/types';
import { clsx } from 'clsx';

import { useFullDestination } from '@jane/bloom/hooks/navigation/useFullDestination';

import styles from './janeGoldRow.module.css';

export interface JaneGoldRowProps {
  authenticated?: boolean;
  brandSpecials: BrandSpecials;
  className?: string;
  isFeaturedTab?: boolean;
  mode?: CardCarouselProps['mode'];
  state: string;
  title?: Nullable<string>;
  userHasLinkedBank?: boolean;
}

export function JaneGoldRow({
  authenticated,
  brandSpecials,
  className,
  isFeaturedTab = false,
  mode = 'max-width',
  state,
  title,
  userHasLinkedBank,
}: JaneGoldRowProps) {
  const generatePath = useFullDestination();
  // Don't render if no Jane Gold deals
  if (brandSpecials.length === 0) {
    return null;
  }

  const rowName = title ?? 'Jane Gold rewards';
  const isOhioStore = state === 'Ohio';

  return (
    <CardCarousel
      className={clsx(
        className,
        styles['container'],
        { [styles['featuredContainer']]: isFeaturedTab },
        { [styles['goldTabContainer']]: !isFeaturedTab }
      )}
      cardGap={24}
      cardGapMobile={16}
      cardHeight={272}
      cardHeightMobile={96}
      cardWidth={420}
      cardWidthMobile={326}
      mode={mode}
      name={rowName}
      beforeHeader={
        <Flex alignItems="center">
          {isFeaturedTab && <JaneGoldIcon color="gold" />}
          <Typography
            color="text-main"
            as="h2"
            variant="h2-semibold"
            className={styles['janeGoldTitle']}
          >
            {rowName}
          </Typography>
        </Flex>
      }
      subtitle={
        isOhioStore ? (
          <OhioGoldEducation mt={4} userHasLinkedBank={userHasLinkedBank} />
        ) : undefined
      }
    >
      {brandSpecials.map((special, index) => (
        <CardCarousel.Card key={`${special.id}-${index}`}>
          <JaneGoldCard
            authenticated={authenticated}
            brandImage={special.product_brand.logo_url}
            brandName={special.product_brand.name}
            imageSrc={special.photo?.urls?.original}
            isBundle={special.special_type !== 'product'}
            rules={special.rules}
            specialType={special.special_type}
            tagLabel={special.discount_label}
            title={special.title}
            to={generatePath(
              `/jane-gold/${special.id}/${getSpecialSlug(special.title)}`
            )}
          />
        </CardCarousel.Card>
      ))}
    </CardCarousel>
  );
}
